import { EstimateStatuses } from '../../../common/constants'
import { PendingMove } from '../../JobKanban/jobKanbanDragModel'
import { isUnLosing, KanbanColumn } from '../../JobKanban/jobKanbanModel'
import { JobListItem } from '../../JobList/jobListItem'
import { BoardSortColumn, boardSortValue } from '../../JobList/jobListFiltersModel'
import { EstimateReportData, EstimatesSummary } from './estimatesReportApi'

/* The report already returns estimates grouped by status, which is the shape a board needs.
   Reading it here rather than the jobs list is what lets the board sit under the report's own
   filter bar and summary: the server has already applied every filter, so the two views cannot
   disagree about which records they are showing. */
export const estimateGroupForStatus: Record<number, keyof EstimatesSummary> = {
  [EstimateStatuses.Unsent]: 'unsent',
  [EstimateStatuses.Sent]: 'sent',
  [EstimateStatuses.Approved]: 'approved',
  [EstimateStatuses.Lost]: 'lost'
}

export const toCard = (estimate: EstimateReportData, status: number): JobListItem => ({
  id: estimate.projectId,
  jobNumber: estimate.jobNo,
  soldDate: '',
  estimateDateUtc: estimate.estimateDateUtc,
  name: estimate.jobName,
  customer: estimate.displayName,
  property: estimate.property ?? [estimate.address1, estimate.city, estimate.state].filter(Boolean).join(', '),
  finalPrice: estimate.price,
  salesPerson: estimate.salesPersonName,
  status: 0 as never,
  estimateStatus: status as never,
  estimateStatusDateUtc: estimate.estimateDateUtc,
  url: `estimates/${estimate.projectId}`,
  active: true
})

export const cardsForStatus = (estimates: EstimatesSummary, status: number): JobListItem[] =>
  ((estimates[estimateGroupForStatus[status]] ?? []) as EstimateReportData[])
    .map(estimate => toCard(estimate, status))

/* What one column shows, once optimistic moves are folded in. The jobs board does the same
   job through applyPendingMoves; this is the report-shaped equivalent.

   The keptIds guard is the part that matters. After the write succeeds the board awaits the
   report refetch and then drops the pending move — two state updates in an async continuation,
   which React 17 does not batch. In the render between them the server already reports the
   estimate in its new column while the pending copy is still held, and without this filter
   both render under the same key. */
export const cardsForColumn = (
  estimates: EstimatesSummary, pendingMoves: PendingMove[], status: number
): JobListItem[] => {
  const movedAway = new Set(pendingMoves
    .filter(move => move.from === status && move.to !== status)
    .map(move => move.job.id))
  const kept = cardsForStatus(estimates, status).filter(card => !movedAway.has(card.id))

  const keptIds = new Set(kept.map(card => card.id))
  const incoming = pendingMoves
    .filter(move => move.to === status && !keptIds.has(move.job.id))
    .map(move => move.job)

  return [...incoming, ...kept]
}

export type EstimatesBoardSort = {
  column: string
  descending: boolean
}

export const defaultEstimatesBoardSort = { column: 'estimateDateUtc' as BoardSortColumn, descending: true }

export const sortCards = (cards: JobListItem[], sortColumn: BoardSortColumn, descending: boolean): JobListItem[] => {
  const direction = descending ? -1 : 1
  const valueOf = boardSortValue[sortColumn]
  return [...cards].sort((left, right) => {
    const leftValue = valueOf(left)
    const rightValue = valueOf(right)
    if (typeof leftValue === 'number' && typeof rightValue === 'number') {
      return (leftValue - rightValue) * direction
    }
    return String(leftValue).localeCompare(String(rightValue)) * direction
  })
}

/* Lost is the only estimate status a drop can set, so it is the only column that accepts one —
   except that a card already in Lost has to be able to come back out, or one mistaken drop is
   permanent. That exception is isUnLosing, borrowed rather than restated. */
export const columnTakesTheCard = (column: KanbanColumn, draggingStatus?: number) =>
  column.acceptsDrops
    || (draggingStatus !== undefined && isUnLosing(false, draggingStatus, column.status))

/* Counted here rather than taken from the server, because the report returns the whole window
   in one go. These are the same arrays the Summary totals, so the column headers and the
   Summary cannot drift apart. */
export const columnTotal = (cards: JobListItem[]) =>
  cards.reduce((total, card) => total + card.finalPrice, 0)
